import { useEffect, useMemo, useState } from "react";
import {
  Download,
  Loader2,
  Users,
  Search,
  Megaphone,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../../lib/api";

/**
 * RallyTab — Admin dashboard view for Rally Of The Minds RSVPs.
 *
 * Shows: live RSVP count, by-state leaderboard, searchable table, CSV export.
 */

const INTEREST_LABELS = {
  attend: "In person",
  livestream: "Livestream",
  speaker: "Speaker",
  volunteer: "Volunteer",
  donate: "Sponsor",
  performer: "Performer",
  vendor: "Vendor",
  press: "Press",
};

function StatCard({ label, value, hint, dataTestid }) {
  return (
    <div
      className="bg-bdu-surface2 border border-bdu-gold/15 p-5"
      data-testid={dataTestid}
    >
      <div className="label-eyebrow">{label}</div>
      <div className="mt-2 font-serif text-3xl text-bdu-cream">{value}</div>
      {hint && <div className="mt-1 text-xs text-bdu-cream/50">{hint}</div>}
    </div>
  );
}

export default function RallyTab() {
  const [data, setData] = useState({ rows: [], total: 0, goal: 500 });
  const [stats, setStats] = useState({ total: 0, goal: 500, by_state: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/rally/signups", { params: { q: q || undefined, page, page_size: pageSize } }),
      api.get("/rally/stats"),
    ])
      .then(([a, b]) => {
        setData(a.data);
        setStats(b.data);
        setErr(null);
      })
      .catch((e) => {
        const s = e?.response?.status;
        if (s === 401 || s === 403) {
          setErr("Your admin session has expired. Please log in again.");
        } else {
          setErr(formatApiError(e?.response?.data?.detail) || "Failed to load RSVPs");
        }
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  const progressPct = Math.min(100, Math.round(100 * (stats.total || 0) / Math.max(1, stats.goal || 500)));

  const downloadCSV = () => {
    const token = localStorage.getItem("bdu_token");
    const url = `${API}/admin/rally/export.csv`;
    // Bearer auth isn't attachable to anchor-href downloads cross-origin.
    // Use fetch + blob so admin auth works through the preview ingress.
    toast.loading("Preparing CSV…", { id: "rally-csv" });
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        const dl = URL.createObjectURL(blob);
        a.href = dl;
        a.download = "rally_of_the_minds_signups.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(dl);
        toast.success("CSV downloaded", { id: "rally-csv" });
      })
      .catch((e) => toast.error(`Download failed: ${e.message}`, { id: "rally-csv" }));
  };

  const rows = data.rows || [];

  return (
    <div className="space-y-8" data-testid="admin-rally-tab">
      {/* Header + stats */}
      <div className="flex items-start justify-between flex-wrap gap-6">
        <div>
          <div className="flex items-center gap-2 label-eyebrow">
            <Megaphone size={14} className="text-bdu-gold" /> Rally Of The Minds · Launch RSVPs
          </div>
          <h2 className="mt-3 font-serif text-3xl text-bdu-cream">The rally roll.</h2>
          <p className="mt-2 text-sm text-bdu-cream/60 max-w-xl">
            Live sign-ups for the Rise II Power rally. Lock the date the moment the roll crosses the goal.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={load}
            className="btn-ghost text-xs"
            data-testid="admin-rally-refresh-btn"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={downloadCSV}
            className="btn-gold text-xs"
            data-testid="admin-rally-csv-btn"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Progress + stat tiles */}
      <div>
        <div className="h-2 bg-bdu-cream/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-bdu-gold to-amber-300"
            style={{ width: `${progressPct}%`, transition: "width 900ms cubic-bezier(0.4,0,0.2,1)" }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[0.65rem] uppercase tracking-widest text-bdu-cream/50">
          <span>{stats.total || 0} on the roll</span>
          <span>Target · {stats.goal || 500}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total RSVPs" value={stats.total || 0} dataTestid="admin-rally-stat-total" />
        <StatCard label="Progress" value={`${progressPct}%`} hint={`of ${stats.goal || 500} goal`} dataTestid="admin-rally-stat-progress" />
        <StatCard
          label="Top State"
          value={stats.by_state?.[0]?.state || "—"}
          hint={stats.by_state?.[0] ? `${stats.by_state[0].count} RSVPs` : null}
          dataTestid="admin-rally-stat-topstate"
        />
        <StatCard label="States Reached" value={stats.by_state?.length || 0} dataTestid="admin-rally-stat-states" />
      </div>

      {/* By-state leaderboard */}
      {stats.by_state && stats.by_state.length > 0 && (
        <div className="bg-bdu-surface2 border border-bdu-gold/15 p-5" data-testid="admin-rally-by-state">
          <div className="label-eyebrow">Reach · top states</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {stats.by_state.map((row) => (
              <div
                key={row.state}
                className="bg-black/40 border border-bdu-gold/20 px-3 py-1.5 text-xs text-bdu-cream flex items-center gap-2"
              >
                <span className="font-serif text-bdu-gold">{row.state}</span>
                <span className="text-bdu-cream/55">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search + table */}
      <div className="bg-bdu-surface2 border border-bdu-gold/15">
        <div className="flex items-center gap-3 border-b border-bdu-gold/10 px-5 py-4">
          <Search size={14} className="text-bdu-cream/50" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { setPage(1); load(); }
            }}
            placeholder="Search name, email, city, state, ZIP…"
            className="flex-1 bg-transparent focus:outline-none text-sm text-bdu-cream placeholder:text-bdu-cream/30"
            data-testid="admin-rally-search"
          />
          <button
            type="button"
            onClick={() => { setPage(1); load(); }}
            className="text-xs uppercase tracking-widest text-bdu-gold hover:underline"
            data-testid="admin-rally-search-btn"
          >
            Search
          </button>
        </div>

        {err && (
          <div className="px-5 py-4 text-sm text-red-200 bg-red-500/10">
            {err}
          </div>
        )}

        {loading && !data.rows?.length ? (
          <div className="flex items-center justify-center py-16 text-bdu-cream/50">
            <Loader2 className="animate-spin mr-3" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-bdu-cream/50" data-testid="admin-rally-empty">
            <Users size={32} className="mx-auto mb-3 opacity-40" />
            <div>No RSVPs yet.</div>
            <div className="mt-2 text-xs text-bdu-cream/40">
              Share <code className="text-bdu-gold">/rally</code> on your socials to kick things off.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-rally-table">
              <thead className="text-[0.6rem] uppercase tracking-widest text-bdu-cream/50 border-b border-bdu-gold/10">
                <tr>
                  <th className="text-left py-3 px-4">When</th>
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Contact</th>
                  <th className="text-left py-3 px-4">Location</th>
                  <th className="text-left py-3 px-4">Interests</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bdu-gold/5">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-black/30" data-testid={`admin-rally-row-${r.id}`}>
                    <td className="py-3 px-4 text-xs text-bdu-cream/60 whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-3 px-4 text-bdu-cream">{r.name}</td>
                    <td className="py-3 px-4 text-xs">
                      <a href={`mailto:${r.email}`} className="text-bdu-gold hover:underline inline-flex items-center gap-1">
                        <Mail size={10} /> {r.email}
                      </a>
                      {r.phone && (
                        <div className="text-bdu-cream/55 mt-0.5">{r.phone}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-bdu-cream/70 whitespace-nowrap">
                      {[r.city, r.state, r.zip].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {(r.interests || []).map((k) => (
                          <span
                            key={k}
                            className="text-[0.6rem] uppercase tracking-widest text-bdu-cream bg-bdu-gold/15 border border-bdu-gold/30 px-2 py-0.5"
                          >
                            {INTEREST_LABELS[k] || k}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-bdu-gold/10 px-5 py-3 text-xs">
            <div className="text-bdu-cream/50">
              Page {data.page || 1} of {totalPages} · {data.total || 0} total
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-ghost text-xs disabled:opacity-30"
                data-testid="admin-rally-prev-btn"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-ghost text-xs disabled:opacity-30"
                data-testid="admin-rally-next-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
